import { useState, type ReactNode } from 'react'
import { Link, usePage } from '@inertiajs/react'
import NotificationBell from '@/components/dashboard/NotificationBell'

export type NavItem = {
  label: string
  href: string
  /** 既存の管理画面（ERB画面）への遷移 */
  external: boolean
  icon: 'home' | 'pie' | 'user' | 'cash' | 'gear' | 'info'
}

export type DashboardSharedProps = {
  user_label: string
  show_notification_bell: boolean
  nav_items: NavItem[]
  nav_paths: {
    home: string
    examinations: string
    consultations: string
    doctor_schedules: string
    clinic_schedules: string
    profile_edit?: string | null
  }
  flash: {
    notice?: string
    alert?: string
  }
  header: {
    logo_path: string
    logout_path: string
  }
}

// 旧サイドバー（_sidebar.html.erb）と同じアイコン
const ICON_PATHS: Record<NavItem['icon'], string[]> = {
  home: [
    'M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z',
  ],
  pie: [
    'M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z',
    'M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z',
  ],
  user: [
    'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
  ],
  cash: [
    'M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4z',
    'M14 6a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h6zm-3 5a2 2 0 100-4 2 2 0 000 4z',
  ],
  gear: [
    'M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947z M10 13a3 3 0 100-6 3 3 0 000 6z',
  ],
  info: [
    'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
  ],
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

/**
 * 管理画面Inertiaページの共通レイアウト (#1070, #1135)
 * 旧管理画面と行き来してもストレスがないよう、ヘッダー（固定・ロゴ・「管理画面」・
 * 右上ログアウト）とサイドバー（固定・項目・並び・アイコン・スタイル）を
 * _header.html.erb / _sidebar.html.erb と同じ見た目で再現する。
 * サイドバーの構成はロールごとにサーバー側（DoctorLayoutProps concern）から渡す。
 */
export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { user_label, show_notification_bell, nav_items, flash, header } =
    usePage().props as unknown as DashboardSharedProps
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const itemClass =
    'flex items-center p-2 text-base text-gray-900 rounded-lg hover:bg-gray-100 group'

  const confirmLogout = (e: React.FormEvent<HTMLFormElement>) => {
    if (!window.confirm('ログアウトしますか？')) e.preventDefault()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="fixed z-30 w-full bg-white border-b border-gray-200">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start">
              <button
                type="button"
                aria-label="メニューを開閉"
                onClick={() => setSidebarOpen((v) => !v)}
                className="p-2 text-gray-600 rounded cursor-pointer lg:hidden hover:text-gray-900 hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
              <a href="/dashboard" className="flex ml-2 md:mr-24">
                <img src={header.logo_path} alt="おうち病院" className="h-6 sm:h-8 mr-2 sm:mr-3" />
                <span className="self-center text-base sm:text-2xl font-semibold whitespace-nowrap text-[#2196F3]">管理画面</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              {show_notification_bell && <NotificationBell />}
              <span className="hidden sm:inline text-sm text-gray-600 whitespace-nowrap">{user_label}</span>
              <form method="post" action={header.logout_path} onSubmit={confirmLogout}>
                <input type="hidden" name="_method" value="delete" />
                <input type="hidden" name="authenticity_token" value={typeof document === 'undefined' ? '' : csrfToken()} />
                <button type="submit" className="cursor-pointer whitespace-nowrap">ログアウト</button>
              </form>
            </div>
          </div>
        </div>
      </nav>
      <aside
        className={`fixed top-0 left-0 z-20 flex-col flex-shrink-0 w-64 h-full pt-16 font-normal ${sidebarOpen ? 'flex' : 'hidden'} lg:flex`}
        aria-label="Sidebar"
      >
        <div className="relative flex flex-col flex-1 min-h-0 pt-0 bg-white border-r border-gray-200">
          <div className="flex flex-col flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="flex-1 px-3 space-y-1 bg-white">
              <ul className="pb-2 space-y-2">
                {nav_items.map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a href={item.href} className={itemClass}>
                        <NavIcon paths={ICON_PATHS[item.icon]} />
                        <span className="ml-3">{item.label}</span>
                      </a>
                    ) : (
                      <Link href={item.href} className={itemClass}>
                        <NavIcon paths={ICON_PATHS[item.icon]} />
                        <span className="ml-3">{item.label}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </aside>
      <main className="lg:ml-64 pt-16">
        <div className="p-4 md:p-8">
          {flash?.notice && (
            <div className="mb-4 px-4 py-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg">
              {flash.notice}
            </div>
          )}
          {flash?.alert && (
            <div className="mb-4 px-4 py-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg">
              {flash.alert}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  )
}

function NavIcon({ paths }: { paths: string[] }) {
  return (
    <svg
      className="w-6 h-6 text-gray-500 transition duration-75 group-hover:text-gray-900"
      fill="currentColor"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}
